import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api, { creditsAPI } from '../../services/api';
import { CreditStatus } from '../../types';
import type { Lead, CallSummary, RunProgress, RunExecutionStatus } from '../RunProgressPage/runProgressTypes';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import {
  brisbaneInputToUtcIso,
  getDefaultBrisbaneInput,
  isBrisbaneInputInFuture,
  utcIsoToBrisbaneInput,
} from '../../utils/brisbaneDateTime';

export function useRunCampaignPage() {
  const { runId } = useParams<{ runId: string }>();
  const [run, setRun] = useState<RunProgress | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [callSummaries, setCallSummaries] = useState<CallSummary[]>([]);
  const [executionStatus, setExecutionStatus] = useState<RunExecutionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [webCallUrl, setWebCallUrl] = useState<string | null>(null);
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);
  const [maxRetryAttempts, setMaxRetryAttempts] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [scheduledAtInput, setScheduledAtInput] = useState(getDefaultBrisbaneInput());
  const refreshInterval = useRef<NodeJS.Timeout | null>(null);

  const loadCreditStatus = async () => {
    try {
      const response = await creditsAPI.getBalance();
      const convertedStatus: CreditStatus = {
        available: response.available_credits,
        total_used: response.total_consumed,
        call_limit: response.available_credits + response.total_consumed,
        subscription_status: 'active',
        credit_status: response.available_credits > 10 ? 'sufficient' :
          response.available_credits > 0 ? 'low' : 'exhausted',
        percentage_used: response.total_purchased > 0 ?
          Math.round((response.total_consumed / response.total_purchased) * 100) : 0
      };
      setCreditStatus(convertedStatus);
    } catch (err) {
    }
  };

  const loadRunProgress = async (showLoading = true) => {
    if (!runId) return;
    try {
      if (showLoading) setLoading(true);
      const [runResp, detailedResp] = await Promise.all([
        api.get(`/runs/${runId}`),
        api.get(`/runs/${runId}/detailed-summary`).catch(() => null),
      ]);
      const merged = { ...runResp.data };
      if (detailedResp?.data) {
        merged.disposition_breakdown = detailedResp.data.disposition_breakdown;
        merged.temperature_breakdown = detailedResp.data.temperature_breakdown;
        merged.goal_achievement_rate = detailedResp.data.goal_achievement_rate;
        merged.follow_ups_pending = detailedResp.data.follow_ups_pending;
      }
      setRun(merged);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to load run');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadLeads = async () => {
    if (!runId) return;
    try {
      const response = await api.get(`/runs/${runId}/leads`);
      setLeads(response.data);
    } catch (_) {}
  };

  const loadStatuses = async () => {
    if (!runId) return;
    try {
      const response = await api.get(`/runs/${runId}/statuses`);
      const map = new Map<string, { call_status: string; call_id?: string; call_started_at?: string | null }>();
      (response.data?.statuses || []).forEach((s: any) => {
        map.set(s.lead_id, { call_status: s.call_status, call_id: s.call_id, call_started_at: s.call_started_at });
      });
      setLeads(prev => prev.map(l => {
        const s = map.get(l.id);
        return s ? { ...l, call_status: s.call_status || l.call_status, call_id: s.call_id ?? l.call_id, call_started_at: s.call_started_at ?? l.call_started_at } : l;
      }));
    } catch (_) {}
  };

  const loadCallSummaries = async () => {
    if (!runId) return;
    try {
      const response = await api.get(`/runs/${runId}/call-summaries`);
      setCallSummaries(response.data);
    } catch (_) {}
  };

  const loadRetryConfig = async () => {
    if (!runId) return;
    try {
      const response = await api.get(`/runs/${runId}/retry-stats`);
      const configured = response.data?.retry_configuration?.max_retry_count;
      if (typeof configured === 'number') setMaxRetryAttempts(configured);
    } catch (_) {}
  };

  const loadExecutionStatus = async () => {
    if (!runId || run?.status !== 'running') return;
    try {
      const response = await api.get(`/runs/${runId}/status`);
      setExecutionStatus(response.data);
    } catch (_) {}
  };

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    setToast({ type, message });
  };

  const handleExportCallSummaries = async () => {
    if (!runId) return;
    try {
      setExporting(true);
      const response = await api.post(`/runs/${runId}/export-call-summaries`);
      const downloadUrl = `/runs/${runId}/download-export/${response.data.filename}`;
      const fileResponse = await api.get(downloadUrl, { responseType: 'blob' });
      const blob = new Blob([fileResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('success', 'Export started. Your file is downloading.');
      track(AnalyticsEvents.RUN_EXPORT_SUMMARIES, { run_id: runId });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to export call summaries';
      setError(`Export failed: ${errorMessage}`);
      showToast('error', `Export failed: ${errorMessage}`);
    } finally {
      setExporting(false);
    }
  };

  const startRun = async () => {
    if (!runId) return;
    if (creditStatus?.credit_status === 'exhausted') {
      showToast('error', 'Cannot start: credits exhausted.');
      return;
    }
    try {
      setLoading(true);
      await api.post(`/runs/${runId}/start`);
      await Promise.all([loadRunProgress(true), loadLeads(), loadStatuses(), loadCreditStatus()]);
      showToast('success', 'Campaign started. Calls are being dispatched.');
      track(AnalyticsEvents.RUN_STARTED, { run_id: runId });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to start campaign';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const stopRun = async () => {
    if (!runId) return;
    try {
      setLoading(true);
      await api.post(`/runs/${runId}/stop`);
      await loadRunProgress(true);
      showToast('success', 'Campaign stopped.');
      track(AnalyticsEvents.RUN_STOPPED, { run_id: runId });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to stop campaign';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const saveSchedule = async (): Promise<{ ok: boolean; message?: string }> => {
    if (!runId) return { ok: false };
    if (!isBrisbaneInputInFuture(scheduledAtInput)) {
      showToast('error', 'Scheduled time must be in the future (Brisbane time).');
      return { ok: false };
    }
    const successMessage = run?.status === 'scheduled' ? 'Schedule updated.' : 'Campaign scheduled.';
    try {
      setLoading(true);
      await api.post(`/runs/${runId}/schedule`, {
        scheduled_at: brisbaneInputToUtcIso(scheduledAtInput),
      });
      await loadRunProgress(true);
      return { ok: true, message: successMessage };
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to schedule campaign';
      setError(errorMessage);
      showToast('error', errorMessage);
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  const cancelSchedule = async () => {
    if (!runId) return;
    try {
      setLoading(true);
      await api.post(`/runs/${runId}/cancel-schedule`);
      await loadRunProgress(true);
      setScheduledAtInput(getDefaultBrisbaneInput());
      showToast('success', 'Campaign schedule canceled.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to cancel schedule';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const retryCall = async (leadId: string) => {
    if (!runId) return;
    const lead = leads.find(l => l.id === leadId);
    if (maxRetryAttempts !== null && lead && lead.retry_count >= maxRetryAttempts) {
      showToast('info', 'Retry limit reached for this lead.');
      return;
    }
    if (creditStatus?.credit_status === 'exhausted') {
      showToast('error', 'Cannot retry: credits exhausted.');
      return;
    }
    try {
      setLoading(true);
      await api.post(`/runs/${runId}/leads/${leadId}/retry`);
      await Promise.all([loadLeads(), loadStatuses(), loadCallSummaries(), loadCreditStatus()]);
      showToast('success', 'Retry initiated.');
      track(AnalyticsEvents.RUN_LEAD_RETRY, { run_id: runId, lead_id: leadId });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to retry call';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cutCall = async (leadId: string) => {
    if (!runId) return;
    try {
      setLoading(true);
      await api.post(`/runs/${runId}/leads/${leadId}/cut`);
      await Promise.all([loadLeads(), loadStatuses(), loadCallSummaries()]);
      showToast('success', 'Call terminated.');
      track(AnalyticsEvents.RUN_LEAD_CUT, { run_id: runId, lead_id: leadId });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to terminate call';
      setError(errorMessage);
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-emerald-400 bg-emerald-500/20 border-emerald-400/30';
      case 'inbound_completed': return 'text-teal-400 bg-teal-500/20 border-teal-400/30';
      case 'failed': return 'text-red-400 bg-red-500/20 border-red-400/30';
      case 'in_progress': return 'text-blue-400 bg-blue-500/20 border-blue-400/30';
      case 'pending': return 'text-yellow-400 bg-yellow-500/20 border-yellow-400/30';
      case 'dialing': return 'text-indigo-400 bg-indigo-500/20 border-indigo-400/30';
      case 'ringing': return 'text-cyan-400 bg-cyan-500/20 border-cyan-400/30';
      case 'no_answer': return 'text-orange-400 bg-orange-500/20 border-orange-400/30';
      case 'busy': return 'text-fuchsia-400 bg-fuchsia-500/20 border-fuchsia-400/30';
      case 'voicemail': return 'text-amber-400 bg-amber-500/20 border-amber-400/30';
      case 'canceled': return 'text-gray-400 bg-gray-500/20 border-gray-400/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-400/30';
    }
  };

  const formatCallDuration = (startedAt: string | null | undefined): string => {
    if (!startedAt) return '';
    const start = new Date(startedAt).getTime();
    const elapsedSec = Math.floor((Date.now() - start) / 1000);
    const m = Math.floor(elapsedSec / 60);
    const s = elapsedSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getRunStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'text-gray-400 bg-gray-500/20 border-gray-400/30';
      case 'scheduled': return 'text-cyan-400 bg-cyan-500/20 border-cyan-400/30';
      case 'running': return 'text-emerald-400 bg-emerald-500/20 border-emerald-400/30';
      case 'stopped': return 'text-red-400 bg-red-500/20 border-red-400/30';
      case 'paused': return 'text-yellow-400 bg-yellow-500/20 border-yellow-400/30';
      case 'completed': return 'text-blue-400 bg-blue-500/20 border-blue-400/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-400/30';
    }
  };

  useEffect(() => {
    if (run?.scheduled_at) {
      setScheduledAtInput(utcIsoToBrisbaneInput(run.scheduled_at));
    } else if (!runId) {
      setScheduledAtInput(getDefaultBrisbaneInput());
    }
  }, [run?.scheduled_at, runId]);

  useEffect(() => {
    if (runId) {
      loadRunProgress(true);
      loadLeads();
      loadCallSummaries();
      loadStatuses();
      loadCreditStatus();
      loadRetryConfig();
      track(AnalyticsEvents.RUN_VIEWED, { run_id: runId });
    }
    if (autoRefresh) {
      const intervalMs = run && run.status === 'running' ? 2000 : 3000;
      refreshInterval.current = setInterval(() => {
        if (run && run.status === 'running') {
          loadRunProgress(false);
          loadLeads();
          loadCallSummaries();
          loadStatuses();
          loadExecutionStatus();
        } else {
          loadRunProgress(false);
          loadLeads();
          loadCallSummaries();
          loadStatuses();
        }
      }, intervalMs);
    }
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [runId, autoRefresh, run?.status]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  return {
    runId,
    run,
    leads,
    callSummaries,
    executionStatus,
    loading,
    error,
    autoRefresh,
    setAutoRefresh,
    webCallUrl,
    currentCallId,
    creditStatus,
    maxRetryAttempts,
    exporting,
    toast,
    scheduledAtInput,
    setScheduledAtInput,
    scheduleValidationError: !isBrisbaneInputInFuture(scheduledAtInput) ? 'Scheduled time must be in the future (Brisbane time).' : '',
    showToast,
    loadRunProgress,
    loadLeads,
    loadStatuses,
    loadCallSummaries,
    loadCreditStatus,
    startRun,
    saveSchedule,
    cancelSchedule,
    stopRun,
    retryCall,
    cutCall,
    handleExportCallSummaries,
    getStatusColor,
    formatCallDuration,
    getRunStatusColor,
  };
}
